<script module lang="ts">
  // Login form (spec §7.7): email + password → store.login(); errors mapped from ApiError.
  import { ApiError } from '$lib/api/client';
  import type { I18nKey } from '$lib/i18n';

  /** `429 → rateLimited`, `0 → netError`, `too_many_connections`, other ApiError → badLogin. */
  export function loginErrorKey(err: unknown): I18nKey {
    if (err instanceof ApiError) {
      if (err.status === 429) return 'rateLimited';
      if (err.status === 0) return 'netError';
      if (err.code === 'too_many_connections') return 'tooManyConnections';
      return 'badLogin';
    }
    return 'netError';
  }
</script>

<script lang="ts">
  import { t } from '$lib/i18n';
  import { store } from '$lib/store';
  import { tenant } from '$lib/store/tenant.svelte';

  let email = $state('');
  let password = $state('');
  let busy = $state(false);
  let error = $state<string | null>(null);
  let emailEl = $state<HTMLInputElement | null>(null);

  async function submit(ev: SubmitEvent) {
    ev.preventDefault();
    if (busy) return;
    busy = true;
    error = null;
    try {
      await store.login(email.trim(), password);
    } catch (err) {
      error = t(loginErrorKey(err));
    } finally {
      busy = false;
    }
  }

  $effect(() => {
    emailEl?.focus();
  });
</script>

<div class="wrap">
  <form class="card" onsubmit={submit}>
    <img src="/img/logo.svg" alt="" width="56" height="56" />
    <h1>{tenant.name ?? t('appName')}</h1>
    <label>
      <span>{t('email')}</span>
      <input type="email" dir="ltr" autocomplete="username" spellcheck="false" required placeholder={tenant.loginHint} bind:value={email} bind:this={emailEl} />
    </label>
    <label>
      <span>{t('password')}</span>
      <input type="password" autocomplete="current-password" required bind:value={password} />
    </label>
    {#if error}<p class="err" role="alert">{error}</p>{/if}
    <button type="submit" class="primary" disabled={busy}>{busy ? t('signingIn') : t('signIn')}</button>
  </form>
</div>

<style>
  /* @css auth/Login start */
  .wrap {
    min-height: 100%;
    display: grid;
    place-items: center;
    background: var(--surface-subtle);
    padding: var(--space-4)
  }

  .card {
    width: min(380px, 100%);
    background: var(--surface-card);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-panel);
    box-shadow: var(--shadow-dialog);
    padding: var(--space-8);
    display: grid;
    gap: var(--space-4);
    justify-items: center
  }

  h1 {
    font-size: var(--text-h4);
    color: var(--text-strong)
  }

  label {
    display: grid;
    gap: var(--space-1);
    width: 100%
  }

  label span {
    font-size: var(--text-sm);
    color: var(--text-muted)
  }

  input {
    block-size: var(--control-lg);
    padding: 0 var(--space-4);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-control);
    background: var(--surface-card);
    color: var(--text-body);
    width: 100%;
    transition: border-color var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard)
  }

  input:focus {
    border-color: var(--blue-500);
    box-shadow: var(--ring-focus);
    outline: none
  }

  .primary {
    width: 100%;
    block-size: var(--control-lg);
    background: var(--blue-500);
    color: var(--text-on-accent);
    border-radius: var(--radius-control);
    font-weight: var(--weight-medium);
    transition: background var(--duration-fast) var(--ease-standard)
  }

  .primary:hover {
    background: var(--blue-600)
  }

  .primary:active {
    transform: scale(.99)
  }

  .primary:disabled {
    opacity: .42
  }

  .err {
    color: var(--danger-500);
    font-size: var(--text-sm)
  }

  @media (pointer:fine) {
  .form {
      scrollbar-gutter: stable
    }
  }

  h1 {
    text-wrap: balance
  }

  @media (max-width:480px) {
  .form {
      padding-bottom: calc(2 * var(--control-lg) + var(--space-4) + env(safe-area-inset-bottom))
    }
  }

  @media (max-width:480px) {
  .card {
      min-width: 0
    }
  }

  @media (max-width:480px) {
  :global(.item) .label {
      flex: 1 1 auto;
      min-width: 0
    }
  }
  /* @css auth/Login end */
</style>
